import { InviteMemberResponse } from '@@types/Api/Member';
import Channel, { ThreadChannel } from '@@types/Channel';
import { CHANNEL_ID, MEMBER_ID } from '@@types/common';
import Member, { SharedChannelMember } from '@@types/Member';

import i18n from '~/i18n';
import { getNoMemberTitle, isDirectChannel, isMeChannel, isSharedChannel } from '~/services/channelService';
import GA from '~/services/GA/GA';
import Actions from '~/stores/Actions';
import { channelManager } from '~/stores/ChannelManager';
import { channelReadSeqStore } from '~/stores/ChannelReadSeqStore';
import { channelsStore } from '~/stores/ChannelsStore';
import { channelTenantsStore } from '~/stores/ChannelTenantsStore';
import { interTenantMembersStore } from '~/stores/InterTenantMembersStore';
import { interTenantApi } from '~/stores/providers/InterTenantApi';
import { userStore } from '~/stores/UserStore';
import { showCapacityAlert, validateCapacity } from '~/utils/channelUtil';
import { keyBy, rejectEmpty, rename } from '~/utils/fnUtil/fnUtil';
import { isLeaver } from '~/utils/memberUtil';
import { debounce, isEmpty, noop } from 'lodash';
import {
  andThen,
  find,
  flatten,
  ifElse,
  includes,
  juxt,
  map,
  of,
  pick,
  pipe,
  prop,
  propEq,
  reject,
  tap,
  union,
  uniq,
  useWith as ramdaUseWith,
  when,
} from 'ramda';
import always from 'ramda/es/always';
import identity from 'ramda/es/identity';
import join from 'ramda/es/join';
import pluck from 'ramda/es/pluck';
import { Either, Maybe } from 'ramda-fantasy';

class ChannelMembersActions {
  inviteToChannel = (channelId: string, memberIds: MEMBER_ID[]): Promise<InviteMemberResponse> => {
    if (userStore.isGuest) {
      return Promise.reject();
    }
    const isAdminMode = this.actions.channelActions.checkAdminMode(channelId);
    if (isAdminMode && !channelsStore.isAdmin(channelId, userStore.id)) {
      const description = i18n.t('invite.channel.adminAlert');
      this.actions.modalActions.openAlert({ description });
      return Promise.reject();
    }
    const channel = channelsStore.getById(channelId);
    const totalMembers = union(channel.members, memberIds);
    return validateCapacity(channel.capacity, totalMembers).then(
      () => this.actions.proxyActions.inviteChannelMembers(channelId, memberIds),
      showCapacityAlert
    );
  };

  constructor(private actions: Actions) {}

  fetchChannelReadSeq = (channelId: CHANNEL_ID): void => {
    this.actions.proxyActions
      .fetchChannelReadSeq(channelId)
      .then(keyBy('memberId'))
      .then(channelReadSeqStore.setChannelReadSeq(channelId), noop);
  };

  debouncedFetchChannelReadSeq = debounce((channelId: CHANNEL_ID): void => {
    if (Either.isLeft(channelsStore.safeGetById(channelId))) return;
    this.fetchChannelReadSeq(channelId);
  });

  fetchTenantChannelsMembers = (channels: Channel[]) => {
    return pipe(
      map(ifElse(isDirectChannel, ramdaUseWith<any, any, any>(of, [prop('opponentId')]), prop('members'))),
      flatten,
      uniq,
      rejectEmpty,
      this.actions.memberActions.fetchBulkMembers
    )(channels);
  };

  fetchChannelsMembers = debounce((channels: Channel[]): void => {
    pipe(reject(isSharedChannel), this.fetchTenantChannelsMembers)(channels);
  }, 200);

  fetchInterTenantChannelsMembers = (channels: Channel[]) => {
    return map(this.fetchInterTenantChannelMembers, channels);
  };

  fetchInterTenantChannelMembers = async (channel: Channel): Promise<SharedChannelMember[]> => {
    const promise = this.actions.memberActions.fetchBulkMembers(this.getUserTenantMembers(channel.channelId));

    if (channelTenantsStore.getTenantInfoList(channel.channelId).length > 1) {
      await this.fetchOtherTenantMembers(channel);
    }
    await promise;
    // promise all 과 같은 효과
    return Promise.resolve(interTenantMembersStore.getMembersById(channel.members));
  };

  getOrFetchChannelMembers = (channel: Channel): Promise<SharedChannelMember[]> => {
    return isSharedChannel(channel)
      ? this.getOrFetchInterTenantChannelMembers(channel)
      : this.actions.memberActions.fetchBulkMembers(channel.members);
  };

  getOrFetchInterTenantChannelMembers = async (channel: Channel): Promise<SharedChannelMember[]> => {
    if (isEmpty(channel.members)) {
      await this.actions.channelActions.fetchSharedTenantsList(channel.channelId);
    }
    return pipe<any, any, any, any>(
      prop('members'),
      interTenantMembersStore.getMembersById,
      ifElse<any, any, any>(
        (members) => isEmpty(find<string>(isEmpty)(members)),
        (members: Member[]) => Promise.resolve(members),
        () => this.fetchInterTenantChannelMembers(channel)
      )
    )(channel);
  };

  getChannelMembersName = (channel: Channel): Promise<string> => {
    return pipe<any, Promise<SharedChannelMember[]>, Promise<string>>(
      this.getOrFetchChannelMembers,
      andThen(
        pipe(
          when(always(!isMeChannel(channel)), reject(propEq('id', userStore.id))),
          pluck('name'),
          join(', '),
          ifElse(
            isEmpty,
            () => {
              GA.writeErrorAction('Empty Notify', `[channel.members=${JSON.stringify(channel.members)}]`);
              return getNoMemberTitle();
            },
            identity
          )
        )
      )
    )(channel);
  };

  fetchOtherTenantMembers = (channel: Channel): Promise<SharedChannelMember[]> => {
    const userTenantId = userStore.tenantId;
    return interTenantApi.member
      .fetchChannelMembers(userTenantId, channel.channelId)
      .then(tap(interTenantMembersStore.setMembers(channel.channelId)), always([])) as Promise<SharedChannelMember[]>;
  };

  fetchInterTenantMember = async (channel: Channel, memberId: MEMBER_ID): Promise<SharedChannelMember> => {
    if (isEmpty(channel.members)) {
      // 인터테넌트 채널에는 members 가 없을 수도 있어서 예외처리
      await this.actions.channelActions.fetchSharedTenantsList(channel.channelId);
    }
    return ifElse<any, any, any>(
      includes(memberId),
      () => this.actions.memberActions.fetchMember(memberId),
      () => {
        return this.fetchOtherTenantMembers(channel).then(find(propEq('id', memberId)));
      }
    )(this.getUserTenantMembers(channel.channelId)) as Promise<SharedChannelMember>;
  };

  fetchInterTenantChannelMembersById = (channelId: CHANNEL_ID): void => {
    const channel = channelsStore.getById(channelId);
    channel && this.fetchInterTenantChannelMembers(channel);
  };

  fetchChannelsMembersByChannelIds = (channelIds: CHANNEL_ID[]): void => {
    pipe(map(channelsStore.getById), this.fetchChannelsMembers)(channelIds);
  };

  public updateChannelReadSeq = (channel: Channel) => {
    if (!channel || !this.validateToUpdateReadSeq(channel)) {
      return;
    }
    pipe<any, any, { channelId: CHANNEL_ID; readSeq: number }, void>(
      pick(['channelId', 'seq']),
      rename({ seq: 'readSeq' }),
      juxt([
        (channel) => channelManager.updateChannel({ ...channel, unreadFlag: false } as ThreadChannel),
        this.actions.proxyActions.updateReadSeq,
      ])
    )(channel);
  };

  isLeaverChannel = (channel: Channel): boolean => {
    return (
      isDirectChannel(channel) && Maybe.maybe(false, isLeaver, interTenantMembersStore.safeGetById(channel.opponentId))
    );
  };

  private getUserTenantMembers = (channelId: CHANNEL_ID): MEMBER_ID[] => {
    const userTenantId = userStore.tenantId;
    return channelTenantsStore.getByChannelId(channelId)[userTenantId]?.organizationMemberIds ?? [];
  };

  private validateToUpdateReadSeq = (channel: Channel): boolean => {
    return channel.unreadCount > 0 || channel.readSeq !== channel.seq;
  };
}

export default ChannelMembersActions;
